

import { TimelineSection as TimelineSectionType, TimelineEntry } from "@/types";
import TimelineSectionHeader from "./timeline/TimelineSectionHeader";
import TimelineCollapsedView from "./timeline/TimelineCollapsedView";
import TimelineExpandedView from "./timeline/TimelineExpandedView";
import { motion, AnimatePresence } from "framer-motion";

interface TimelineSectionProps {
  section: TimelineSectionType;
  entries: TimelineEntry[];
  onToggleExpand: (sectionId: string) => void;
  onCreateMemory: (sectionId: string) => void;
  index: number;
  timelineOwnerId?: string;
}

const TimelineSection = ({
  section,
  entries,
  onToggleExpand,
  onCreateMemory,
  index,
  timelineOwnerId
}: TimelineSectionProps) => {
  const isEven = index % 2 === 0;

  // Calculate year range for the section
  const yearFrom = section.yearFrom || 0;
  const yearTo = section.yearTo || new Date().getFullYear();

  return (
    <motion.div
      key={section.id}
      className="w-full relative"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      data-section-id={section.id}
    >
      {/* Timeline Section Header */}
      <TimelineSectionHeader
        section={section}
        isEven={isEven}
        yearFrom={yearFrom}
        yearTo={yearTo}
        handleToggle={() => onToggleExpand(section.id)}
        isExpanded={section.isExpanded || false}
        entriesCount={entries.length}
        onCreateMemory={() => onCreateMemory(section.id)}
      />

      {/* Section Content */}
      <AnimatePresence mode="wait">
        {section.isExpanded ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <TimelineExpandedView 
              entries={entries} 
              isEven={isEven}
              timelineOwnerId={timelineOwnerId}
            />
          </motion.div>
        ) : (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <TimelineCollapsedView isEven={isEven} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TimelineSection;

